import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './Detail.module.scss';
import { isPlainClick } from '../../../utils/linkClick';

export interface Column<T> {
  key: string;
  header: string;
  render: (row: T) => React.ReactNode;
  align?: 'left' | 'right';
}

interface DataTableProps<T> {
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T) => string | number;
  empty: string;
  /** Rows shown before "show all"; the rest are one click away, not paginated. */
  limit?: number;
  onRowClick?: (row: T) => void;
  isSelected?: (row: T) => boolean;
}

/**
 * The Detailmasken's lists: a plain table, optionally cut to its top rows
 * with the rest one click away - the spec keeps every list short on purpose.
 */
function DataTable<T>({ columns, rows, rowKey, empty, limit, onRowClick, isSelected }: DataTableProps<T>) {
  const { t } = useTranslation('templates');
  const [expanded, setExpanded] = useState(false);

  if (rows.length === 0) return <div className={styles.empty}>{empty}</div>;

  const visible = limit && !expanded ? rows.slice(0, limit) : rows;
  const hidden = rows.length - visible.length;

  return (
    <div className={styles.tableWrap}>
      <table className={styles.table}>
        <thead>
          <tr>
            {columns.map((column) => (
              <th key={column.key} className={column.align === 'right' ? styles.alignRight : undefined}>
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {visible.map((row) => (
            <tr
              key={rowKey(row)}
              className={`${onRowClick ? styles.clickableRow : ''} ${isSelected?.(row) ? styles.selectedRow : ''}`}
              // a ctrl/cmd click belongs to a link in the row (new tab), not to the row
              onClick={onRowClick ? (event) => isPlainClick(event) && onRowClick(row) : undefined}
            >
              {columns.map((column) => (
                <td key={column.key} className={column.align === 'right' ? styles.alignRight : undefined}>
                  {column.render(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {(hidden > 0 || expanded) && limit && rows.length > limit && (
        <button type="button" className={styles.linkButton} onClick={() => setExpanded((v) => !v)}>
          {expanded ? t('report.detail.showLess') : t('report.detail.showAll', { count: rows.length })}
        </button>
      )}
    </div>
  );
}

export default DataTable;
